import { connectionError, connectionStatus, gameReady } from '../../state/connection';
import { t } from '../../state/i18n';

const Spinner = () => (
  <div class="relative w-12 h-12">
    {/* Outer ring */}
    <div class="absolute inset-0 rounded-full border-2 border-teal-500/20" />
    {/* Spinning arc */}
    <div class="absolute inset-0 rounded-full border-2 border-transparent border-t-teal-400 animate-spin" />
    {/* Inner glow */}
    <div class="absolute inset-2 rounded-full bg-teal-500/10" />
  </div>
);

const PulsingDot = ({ color }: { color: string }) => (
  <span class="relative flex h-2 w-2">
    <span
      class={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${color}`}
    />
    <span class={`relative inline-flex rounded-full h-2 w-2 ${color}`} />
  </span>
);

const StatusIcon = ({ status }: { status: string }) => {
  if (status === 'connecting') {
    return <Spinner />;
  }

  if (status === 'failed' || status === 'disconnected') {
    return (
      <div class="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
        <svg
          class="w-6 h-6 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    );
  }

  return null;
};

const StatusTitle = ({
  isConnecting,
  isLoading,
  isFailed,
  isDisconnected,
}: {
  isConnecting: boolean;
  isLoading: boolean;
  isFailed: boolean;
  isDisconnected: boolean;
}) => (
  <h1 class="text-2xl font-mono font-semibold tracking-tight text-white/90">
    {isConnecting && t('ui.connection.connecting')}
    {isLoading && t('ui.connection.loadingWorld')}
    {isFailed && t('ui.connection.connectionLost')}
    {isDisconnected && t('ui.connection.disconnected')}
  </h1>
);

const StatusSubtitle = ({
  isConnecting,
  isLoading,
  isFailed,
  isDisconnected,
  error,
}: {
  isConnecting: boolean;
  isLoading: boolean;
  isFailed: boolean;
  isDisconnected: boolean;
  error: string | null;
}) => (
  <div class="flex items-center gap-2 text-slate-400 text-sm">
    {(isConnecting || isLoading) && (
      <>
        <PulsingDot color="bg-teal-400" />
        <span>
          {isConnecting
            ? t('ui.connection.establishingConnection')
            : t('ui.connection.loadingWorld')}
        </span>
      </>
    )}
    {(isFailed || isDisconnected) && error && <span class="text-red-400/80">{error}</span>}
    {isDisconnected && !error && <span>{t('ui.connection.connectionToServerLost')}</span>}
  </div>
);

export const ConnectionStatus = () => {
  const status = connectionStatus.value;
  const error = connectionError.value;
  const isReady = gameReady.value;

  // Don't show anything when connected and game is ready
  if (status === 'connected' && isReady) {
    return null;
  }

  const isConnecting = status === 'connecting';
  const isLoading = status === 'connected' && !isReady;
  const isFailed = status === 'failed';
  const isDisconnected = status === 'disconnected';

  return (
    <div class="fixed inset-0 flex items-center justify-center z-50">
      {/* Animated background */}
      <div class="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <div class="absolute inset-0 opacity-30">
          <div
            class="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-600/20 rounded-full blur-3xl animate-pulse"
            style="animation-duration: 4s"
          />
          <div
            class="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl animate-pulse"
            style="animation-duration: 5s; animation-delay: 1s"
          />
        </div>
      </div>

      {/* Content */}
      <div class="relative flex flex-col items-center gap-8 p-12 animate-fade-in">
        {/* Status icon */}
        <StatusIcon status={isLoading ? 'connecting' : status} />

        {/* Title */}
        <div class="flex flex-col items-center gap-2">
          <StatusTitle
            isConnecting={isConnecting}
            isLoading={isLoading}
            isFailed={isFailed}
            isDisconnected={isDisconnected}
          />
          <StatusSubtitle
            isConnecting={isConnecting}
            isLoading={isLoading}
            isFailed={isFailed}
            isDisconnected={isDisconnected}
            error={error}
          />
        </div>

        {/* Action button for failed/disconnected states */}
        {(isFailed || isDisconnected) && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            class="group relative px-6 py-3 overflow-hidden rounded-lg font-mono text-sm font-medium transition-all duration-300"
          >
            {/* Button background with gradient */}
            <div class="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-500 transition-all duration-300 group-hover:from-teal-500 group-hover:to-teal-400" />
            {/* Shine effect on hover */}
            <div
              class="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-gradient-to-r from-transparent via-white to-transparent -translate-x-full group-hover:translate-x-full"
              style="transition: transform 0.6s"
            />
            {/* Button text */}
            <span class="relative text-white flex items-center gap-2">
              <svg
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t('ui.buttons.reconnect')}
            </span>
          </button>
        )}

        {/* Tip text */}
        {(isFailed || isDisconnected) && (
          <p class="text-slate-500 text-xs font-mono max-w-xs text-center">
            {t('ui.connection.serverHint')}
          </p>
        )}
      </div>
    </div>
  );
};
